import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Clock } from 'lucide-react';

// Session timeout configuration (in milliseconds)
const SESSION_TIMEOUTS = {
  master: 40 * 60 * 1000,  // 40 minutes for master
  client: 20 * 60 * 1000   // 20 minutes for clients
};

const WARNING_TIME = 60 * 1000; // Show warning 1 minute before timeout

const SessionTimer = () => {
  const { user, logout } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState(WARNING_TIME);
  const [totalTimeRemaining, setTotalTimeRemaining] = useState(0); // Full session time
  const [showWarning, setShowWarning] = useState(false);
  const timerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownRef = useRef(null);
  const displayCountdownRef = useRef(null); // For the always-visible timer
  const isInitializedRef = useRef(false);
  const sessionStartRef = useRef(null);

  // Get timeout based on user role
  const getTimeout = () => {
    if (!user) return null;
    return user.role === 'master' ? SESSION_TIMEOUTS.master : SESSION_TIMEOUTS.client;
  };

  // Clear all timers
  const clearAllTimers = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    if (displayCountdownRef.current) {
      clearInterval(displayCountdownRef.current);
      displayCountdownRef.current = null;
    }
  };

  // Setup timers
  const setupTimers = () => {
    const timeout = getTimeout();
    if (!timeout) return;

    clearAllTimers();
    setShowWarning(false);
    setTimeRemaining(WARNING_TIME);
    
    // Set session start time and initial total time
    sessionStartRef.current = Date.now();
    setTotalTimeRemaining(timeout);

    // Start display countdown (updates every second for the visible timer)
    displayCountdownRef.current = setInterval(() => {
      const elapsed = Date.now() - sessionStartRef.current;
      const remaining = Math.max(0, timeout - elapsed);
      setTotalTimeRemaining(remaining);
    }, 1000);

    // Set warning timer (shows warning 1 minute before timeout)
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      setTimeRemaining(WARNING_TIME);
      
      // Start countdown
      countdownRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1000) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);
    }, timeout - WARNING_TIME);

    // Set final logout timer
    timerRef.current = setTimeout(() => {
      clearAllTimers();
      setShowWarning(false);
      logout();
      setTimeout(() => {
        alert('⏱️ Tu sesión ha expirado por inactividad. Por favor, inicia sesión nuevamente.');
      }, 100);
    }, timeout);
  };

  // Handle continue session button click
  const handleContinueSession = () => {
    setupTimers();
  };

  // Initialize and handle activity
  useEffect(() => {
    if (!user) {
      clearAllTimers();
      return;
    }

    const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    const handleActivity = () => {
      // Only reset if warning is not showing
      if (!showWarning) {
        setupTimers();
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial setup - only once (using setTimeout to avoid lint warning)
    if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      setTimeout(setupTimers, 0);
    }

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
    };
    // eslint-disable-next-line
  }, [user, showWarning]);

  // Handle countdown reaching zero
  useEffect(() => {
    if (timeRemaining === 0 && showWarning) {
      // Using setTimeout to avoid lint warning about setState in effect
      setTimeout(() => {
        clearAllTimers();
        setShowWarning(false);
        logout();
        setTimeout(() => {
          alert('⏱️ Tu sesión ha expirado por inactividad. Por favor, inicia sesión nuevamente.');
        }, 100);
      }, 0);
    }
    // eslint-disable-next-line
  }, [timeRemaining, showWarning]);

  // Don't render if no user
  if (!user) return null;

  // Format time remaining
  const formatTime = (ms) => {
    const seconds = Math.ceil(ms / 1000);
    return `${seconds} segundo${seconds !== 1 ? 's' : ''}`;
  };

  // Format time for display (MM:SS)
  const formatDisplayTime = (ms) => {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Get timer color based on time remaining
  const getTimerColor = () => {
    const timeout = getTimeout();
    if (!timeout) return 'bg-gray-500';
    const percentage = (totalTimeRemaining / timeout) * 100;
    if (percentage > 50) return 'bg-green-600';
    if (percentage > 25) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  return (
    <>
      {/* Always Visible Timer Badge */}
      {user && totalTimeRemaining > 0 && (
        <div className="fixed bottom-4 right-4 z-[9998]">
          <div className={`${getTimerColor()} text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-2 transition-colors duration-300`}>
            <Clock className="w-4 h-4" />
            <span className="font-mono font-semibold text-sm">
              {formatDisplayTime(totalTimeRemaining)}
            </span>
            <span className="text-xs opacity-80 hidden sm:inline">
              {user.role === 'master' ? '(Admin)' : '(Cliente)'}
            </span>
          </div>
        </div>
      )}
      {/* Warning Modal */}
      {showWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center">
              <div className="text-6xl mb-4">⏱️</div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Sesión por Expirar
              </h2>
              <p className="text-gray-600 mb-4">
                Tu sesión se cerrará automáticamente por inactividad en:
              </p>
              <div className="text-4xl font-bold text-red-600 mb-6 animate-pulse">
                {formatTime(timeRemaining)}
              </div>
              <p className="text-sm text-gray-500 mb-6">
                Haz clic en el botón para continuar trabajando
              </p>
              <Button 
                onClick={handleContinueSession}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 text-lg"
              >
                ✅ Continuar Sesión
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SessionTimer;
